import React, { useState, useEffect } from 'react';
import { Heart, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { addFavorite, removeFavorite, setPlan, removePlan } from '../../lib/meApi';

/**
 * Floating action group for an event: Favorito + Voy a ir.
 * - Requires login (button still visible, prompts login if not authed).
 * - Reads/persists state to /api/me/*.
 * - Pass `compact` for a smaller layout (card overlays).
 */
export const EngagementButtons = ({
  eventId,
  isFavorite: initialFav = false,
  planStatus: initialPlan = null,
  isAuthenticated = false,
  onLoginRequired,
  compact = false,
  className = '',
}) => {
  const [isFav, setIsFav] = useState(initialFav);
  const [plan, setPlanState] = useState(initialPlan); // null | 'going' | 'maybe'
  const [busy, setBusy] = useState(false);

  useEffect(() => setIsFav(initialFav), [initialFav]);
  useEffect(() => setPlanState(initialPlan), [initialPlan]);

  const requireAuth = () => {
    if (!isAuthenticated) {
      onLoginRequired?.();
      toast.info('Inicia sesión para guardar este evento.');
      return false;
    }
    return true;
  };

  const toggleFav = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!requireAuth() || busy) return;
    setBusy(true);
    const newVal = !isFav;
    setIsFav(newVal); // optimistic
    try {
      if (newVal) await addFavorite(eventId);
      else await removeFavorite(eventId);
      toast.success(newVal ? 'Guardado en Favoritos' : 'Quitado de Favoritos');
    } catch {
      setIsFav(!newVal);
      toast.error('No se pudo actualizar Favoritos');
    } finally {
      setBusy(false);
    }
  };

  const togglePlan = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!requireAuth() || busy) return;
    setBusy(true);
    const newVal = plan === 'going' ? null : 'going';
    setPlanState(newVal); // optimistic
    try {
      if (newVal) await setPlan(eventId, 'going');
      else await removePlan(eventId);
      toast.success(newVal ? 'Agregado a Mis Planes' : 'Quitado de Mis Planes');
    } catch {
      setPlanState(plan);
      toast.error('No se pudo actualizar Mis Planes');
    } finally {
      setBusy(false);
    }
  };

  const size = compact ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = compact ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <div className={`flex gap-2 ${className}`}>
      <button
        type="button"
        onClick={toggleFav}
        disabled={busy}
        title={isFav ? 'Quitar de Favoritos' : 'Guardar en Favoritos'}
        aria-label={isFav ? 'Quitar de Favoritos' : 'Guardar en Favoritos'}
        data-testid={`engagement-fav-${eventId}`}
        className={`${size} rounded-full backdrop-blur-md bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 disabled:opacity-60`}
      >
        <Heart
          className={`${iconSize} transition-colors ${isFav ? 'fill-red-500 text-red-500' : 'text-gray-700'}`}
        />
      </button>
      <button
        type="button"
        onClick={togglePlan}
        disabled={busy}
        title={plan === 'going' ? 'Quitar de Mis Planes' : 'Voy a ir'}
        aria-label={plan === 'going' ? 'Quitar de Mis Planes' : 'Voy a ir'}
        data-testid={`engagement-plan-${eventId}`}
        className={`${size} rounded-full backdrop-blur-md shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 disabled:opacity-60 ${
          plan === 'going'
            ? 'bg-[#00CC66] hover:bg-[#00B359]'
            : 'bg-white/90 hover:bg-white'
        }`}
      >
        <CalendarCheck
          className={`${iconSize} transition-colors ${plan === 'going' ? 'text-white' : 'text-gray-700'}`}
        />
      </button>
    </div>
  );
};

export default EngagementButtons;
